import React, { useMemo } from 'react';
import { useQuery } from 'react-query';
import { ColumnsType } from 'antd/es/table/InternalTable';
import {
	Flex,
	Space,
	Table,
	Typography,
} from 'antd';
import { Link } from 'react-router-dom';
import { standardSort } from '@shared/utils/standardSort';
import {
	FixtureCounterpartyTypes,
	FixtureTypeLabels,
} from '@shared/utils/constants';
import { formatCurrency } from '@shared/utils/currency';
import type { GetPayablesDataResponse } from '@api/features/statistics/getPayablesData';
import { getPayablesData } from '@client/lib/api';
import { useAuth } from '@client/lib/auth';
import VesselDescription from '@client/components/VesselDescription';
import { getFilterProps } from '@client/utils/table';
import { Links } from '@client/utils/links';
import StateTag from '@client/components/StateTag';
import LoadingIndicator from '@client/components/LoadingIndicator';
import { formatDate } from '@client/utils/formatDate';
import { DashboardComponent } from '../helpers/dashboardInfo';
import styles from './PayablesDashboard.module.css';

const PayablesDashboard: DashboardComponent = ({ setLastUpdated }) => {
	const { userInfo } = useAuth();

	const personalVesselCacheKey = userInfo.filterVessels ?
		userInfo.relevantVessels.map((v) => v.id).sort((a, b) => a - b).join(',') :
		'all';

	const {
		data,
		isLoading,
		isFetching,
	} = useQuery(
		['payables', userInfo.filterVessels, personalVesselCacheKey],
		getPayablesData,
		{
			onSuccess: (d) => setLastUpdated(d.lastUpdated),
			refetchOnWindowFocus: false,
		},
	);

	const columns = useMemo(() => {
		if (data == null) {
			return [];
		}

		return [
			{
				title: 'Invoice ID',
				dataIndex: 'invoiceId',
				render: (invoiceId, row) => (
					(invoiceId == null || row.contract.id == null || row.invoiceInternalId == null) ? '-' : (
						<Space size="small">
							<Link to={`${Links.VoyageInvoice.get(row.contract.id, row.invoiceInternalId)}`}>{invoiceId}</Link>
						</Space>
					)
				),
				...getFilterProps((data.data ?? []), 'select', 'invoiceId', 'desc'),
			},
			{
				title: 'Vessel',
				dataIndex: 'vessel',
				render: (vessel) => (
					<VesselDescription vesselId={vessel.id} name={vessel.name} />
				),
				sorter: standardSort(['vessel', 'name']),
				...getFilterProps((data.data.map((d) => d.vessel) ?? []), 'select', 'name', 'desc'),
				onFilter: (value, record) => record.vessel?.name === value,
			},
			{
				title: 'Identifier',
				dataIndex: ['contract'],
				render: (contract) => (
					(contract == null || contract.identifier == null) ? 'No contract' : (
						<Space size="small">
							<Link to={Links.Voyage.get(contract.id)}>{contract.identifier}</Link>
						</Space>
					)
				),
				...getFilterProps((data.data.map((d) => d.contract) ?? []), 'select', 'identifier', 'desc'),
				onFilter: (value, record) => record.contract?.identifier === value,
				sorter: standardSort(['contract', 'identifier']),
			},
			{
				title: 'Contract Type',
				dataIndex: ['contract', 'contractType'],
				render: (type) => FixtureTypeLabels[type as keyof typeof FixtureTypeLabels],
				sorter: standardSort(['contract', 'contractType']),
				...getFilterProps((data.data.map((d) => d.contract) ?? []), 'select', 'contractType', 'desc'),
				onFilter: (value, record) => record.contract?.contractType === value,
			},
			{
				title: 'Contract Counterparty',
				dataIndex: ['counterparties'],
				render: (counterparties) => (
					(counterparties == null || counterparties.length === 0) ? '-' : (
						<Flex gap={5} vertical>
							{counterparties.map((c: { name: string; id: number }) => (
								<Link
									to={Links.Counterparty.get(c.id, FixtureCounterpartyTypes.CHARTEREROWNER)}
								>
									{c.name}
								</Link>
							))}
						</Flex>
					)
				),
				...getFilterProps(
					(data.data.flatMap((d) => d.counterparties).map((c) => ({ chartName: c.name })) ?? []),
					'select', 'chartName', 'desc',
				),
				onFilter: (value, record) => {
					const mapped = record.counterparties.map((c) => c.name);

					return mapped.includes(value.toString());
				},
			},
			{
				title: 'Type',
				dataIndex: 'type',
				...getFilterProps((data.data ?? []), 'select', 'type', 'desc'),
				onFilter: (value, record) => record.type === value,
				width: 100,
			},
			{
				title: 'Description',
				dataIndex: 'description',
				width: 250,
			},
			{
				title: 'Amount',
				dataIndex: 'amount',
				render: (v, row) => formatCurrency(v, row.currency),
				align: 'right',
				sorter: standardSort('amount'),
			},
			{
				title: 'Status',
				dataIndex: 'status',
				render: (v) => (<StateTag state={v} showUnresolved={false} />),
				...getFilterProps((data.data ?? []), 'select', 'status', 'desc'),
				onFilter: (value, record) => record.status === value,
				sorter: (a, b) => a.status.localeCompare(b.status),
			},
			{
				title: 'Due date',
				dataIndex: 'dueDate',
				render: (v) => (v != null ? formatDate(v) : '-'),
				sorter: standardSort('dueDate'),
				width: 100,
			},
			{
				title: 'Invoice date',
				dataIndex: 'invoiceDate',
				render: (v) => (v != null ? formatDate(v) : '-'),
				sorter: standardSort('invoiceDate'),
				width: 100,
			},
			{
				title: 'Paid',
				dataIndex: 'paid',
				render: (v, row) => (v != null ? formatCurrency(v ?? 0, row.currency) : '-'),
				align: 'right',
				sorter: standardSort('paid'),
			},
			{
				title: 'Outstanding',
				dataIndex: 'outstanding',
				render: (v, row) => (v != null ? formatCurrency(v ?? 0, row.currency) : '-'),
				align: 'right',
				sorter: standardSort('outstanding'),
			},
		] as ColumnsType<GetPayablesDataResponse['data'][number]>;
	}, [data]);

	if (isLoading || isFetching || data == null) {
		return (
			<Flex vertical justify="center" align="center" gap={5}>
				<LoadingIndicator fullHeight size="large" />
				<Typography.Title level={3}>Loading data..</Typography.Title>
			</Flex>
		);
	}

	return (
		<Table
			rowKey="id"
			columns={columns}
			dataSource={data?.data}
			className={styles.table}
			pagination={false}
			size="small"
			bordered
			scroll={{ x: true }}
		/>
	);
};

export default PayablesDashboard;
